<template>
  <v-container class="pa-0 hyp-risks-selector-container">
    <v-row v-if="riskTypes && riskTypes.length">
      <v-col cols="12" class="px-0 pt-0">
        <RiskRatingPicker
          :params="params"
          :given-rating="selectedRating"
          :chips="true"
          :clearable="true"
          :hide-cyber-subs="true"
          label="Map Overlay"
          placeholder="Risk Type"
          @change="handleChangeRating"
          @clear="handleClickClearRating"
        ></RiskRatingPicker>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="px-0">
        <div class="v-label mb-2 levels-label">Levels</div>
        <v-range-slider
          v-model="riskScoreRange"
          :tick-labels="riskScoreLabels"
          min="-5"
          max="-1"
          ticks="always"
          tick-size="8"
          track-color="gray"
          track-fill-color="gray"
          class="risk-score-range"
          @change="handleChangeRiskScoreRange"
        ></v-range-slider>
      </v-col>
    </v-row>
    <v-row v-if="regions && regions.length">
      <v-col cols="12" class="px-0 pt-0">
        <v-select
          v-model="selectedRegion"
          :items="regions"
          item-text="text"
          item-value="value"
          label="Regions"
          clearable
          small-chips
          return-object
          class="regions"
          @change="handleChangeRegion"
        ></v-select>
      </v-col>
    </v-row>
    <v-row v-if="geographyTypes && geographyTypes.length > 2">
      <v-col cols="12" class="px-0 py-0">
        <div class="pb-1">Geography Type</div>
        <v-btn-toggle
          mandatory
          color="primary"
          v-model="selectedGeographyType"
          class="mb-5"
          @change="handleGeographyTypeChange"
        >
          <v-btn
            v-for="geographyType in geographyTypes"
            :key="geographyType"
            :value="geographyType"
            x-small
          >
            {{ geographyType }}
          </v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="px-0">
        <v-autocomplete
          v-model="geographyFilters"
          :search-input.sync="search"
          :label="selectedGeographyLabel"
          :items="suggestedItems"
          @keyup="handleKeyUpSearch"
          chips
          small-chips
          clearable
          @change="handleGeographyChange"
          @click:clear="handleClickClearSearch"
          single-line
          dense
          class="search-box"
          prepend-inner-icon="fas fa-search"
        ></v-autocomplete>
      </v-col>
    </v-row>
    <v-row>
      <v-container class="pa-0">
        <v-list two-line class="py-0 list-container">
          <Loader v-if="loading" text="" class="mt-4"></Loader>
          <div
            v-if="!loading && items && !items.length"
            class="text-center pt-5"
          >
            No
            {{
              selectedGeographyType === "All"
                ? "geography"
                : selectedGeographyType.toLowerCase()
            }}
            found
          </div>
          <v-list-item-group
            v-if="!loading && items"
            class="rs-group-container"
            v-model="selected"
          >
            <template v-for="(item, index) in items">
              <v-list-item
                :key="item.code"
                @click="handleClickSelect(item)"
                :title="item.name"
                :ref="item.name"
              >
                <template>
                  <v-list-item-content>
                    <v-list-item-title class="mb-3">
                      <i
                        :title="item.type"
                        v-if="selectedGeographyType === 'All'"
                        class="fa-xs pr-1"
                        :class="getIconByItemType(item)"
                      ></i>
                      {{ item.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle
                      v-html="getItemRisks(item)"
                    ></v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-list-item>
              <v-divider
                v-if="index < items.length - 1"
                :key="index"
              ></v-divider>
            </template>
          </v-list-item-group>
        </v-list>
      </v-container>
    </v-row>
  </v-container>
</template>

<script>
import lodashOrderBy from "lodash/orderBy";
import lodashFind from "lodash/find";
import lodashGet from "lodash/get";
import lodashDebounce from "lodash/debounce";
import lodashFilter from "lodash/filter";
import lodashForOwn from "lodash/forOwn";
import lodashCloneDeep from "lodash/cloneDeep";

import { mapGetters } from "vuex";
import {
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_USER,
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_POWER_OUTAGE,
} from "@/store";
import {
  SAVE_PREFERENCES,
  GET_DETAILS_SIDEBAR,
  GET_SELECTOR_PANEL,
  GET_POWER_OUTAGE_REFRESH_MAP,
} from "@/store/actions.type";
import { MAIN_MAP_ID } from "@/common/constants/maps";
import {
  RISK_LEVELS,
  RISK_RATING_PRIMARIES,
} from "@/common/constants/controlRisks/risks";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import {
  getRiskRatings,
  getRiskRatingCategories,
  addToMap,
  removeFromMap,
  getMapTypeBySelectedRatings,
  populateDefaultPreferences,
} from "@/services/controlRisks/risks.service";
import { getVerifiedEventTopicLayers } from "@/services/events/topics.service";
import { getCountryByCode } from "@/common/maps/utilities";
import { waitForMapReady } from "@/common/maps/maps";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import { removeMapData } from "@/common/maps/features";
import {
  getAllCountries,
  getAllRegions,
  getAllCities,
} from "@/common/maps/utilities";
import { GRIDS_STABILITY_TYPES } from "@/common/constants/grids";
import {
  hasMaritimeAccess,
  hasCitySummaryAccess,
} from "@/services/user/user.service";

const EMITTED_ACTIONS = {
  SELECTED: "selected",
};

const GEOGRAPHY_NAMES = {
  all: "All",
  country: "Country",
  waters: "Waters",
  ports: "Ports",
  cities: "Cities",
};

export default {
  name: "RisksSelector",
  components: {
    Loader: () => import("@/components/common/Loader"),
    RiskRatingPicker: () =>
      import("@/components/controlRisks/risks/RiskRatingPicker"),
  },
  props: {
    enabled: {
      type: Boolean,
      default: false,
    },
    params: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  data() {
    const initialRiskScoreRange = [-5, -1];

    return {
      availableRiskTypes: [],
      availableSubRiskTypes: [],
      risks: null,
      riskTypes: null,
      riskScoreRange: initialRiskScoreRange,
      riskScoreRangePrime: initialRiskScoreRange,
      flattened: [],
      countries: [],
      countriesSeerist: [],
      citiesSeerist: [],
      zones: [],
      waters: [],
      ports: [],
      cities: [],
      items: [],
      search: "",
      suggestedItems: [],
      geographyFilters: [],
      selected: null,
      selectedItem: null,
      selectedRating: null,
      regions: null,
      selectedRegion: null,
      loading: false,
      selectedGeographyType: GEOGRAPHY_NAMES.country,
      selectedGeographyLabel: "Search " + GEOGRAPHY_NAMES.country,
      hasMaritimeAccess: hasMaritimeAccess(),
      hasCitySummaryAccess: hasCitySummaryAccess(),
    };
  },
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
      preferences: MODULE_PREFIX_USER + "preferences",
      selectorPanel: MODULE_PREFIX_HOME + "selectorPanel",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
      refreshDataMap: MODULE_PREFIX_MAP + "refreshData",
      resetMapIcons: MODULE_PREFIX_MAP + "resetMapIcons",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    riskScoreLabels() {
      let labels = [];
      const levels = lodashOrderBy(RISK_LEVELS, ["value"], ["desc"]);
      levels.forEach((level) => {
        labels.push(level.name);
      });
      return labels;
    },
    selectedData() {
      return this.selectorPanel.selectedData;
    },
    geographyTypes() {
      let geographyTypes = [GEOGRAPHY_NAMES.all, GEOGRAPHY_NAMES.country];

      if (this.hasCitySummaryAccess) {
        geographyTypes = geographyTypes.concat([GEOGRAPHY_NAMES.cities]);
      }

      if (this.hasMaritimeAccess) {
        geographyTypes = geographyTypes.concat([GEOGRAPHY_NAMES.ports]);
        geographyTypes = geographyTypes.concat([GEOGRAPHY_NAMES.waters]);
      }

      return geographyTypes;
    },
  },
  mounted() {
    this.initialize();
  },
  methods: {
    async getFilteredRiskTypes() {
      let riskRatingCategories = await getRiskRatingCategories();
      this.riskTypes = riskRatingCategories;
      this.riskTypes.forEach((r) => {
        if (r.parentId) {
          this.availableSubRiskTypes.push(r);
        } else {
          this.availableRiskTypes.push(r);
        }
      });
    },
    getIconByItemType(item) {
      const GEOGRAPHY_TYPES = [
        {
          type: GRIDS_STABILITY_TYPES.country,
          iconClass: "fas fa-flag",
        },
        {
          type: GRIDS_STABILITY_TYPES.city,
          iconClass: "fas fa-city",
        },
        {
          type: GRIDS_STABILITY_TYPES.port,
          iconClass: "fas fa-anchor",
        },
        {
          type: GRIDS_STABILITY_TYPES.water,
          iconClass: "fas fa-water",
        },
      ];
      return lodashFind(GEOGRAPHY_TYPES, ["type", item.type])?.iconClass;
    },
    getItemRisks(item) {
      const risks = lodashOrderBy(item.ratings, "riskCategory.id", "asc");
      let html = `<div style="font-size:11px;">`;
      let htmlRatings = "";
      let itemRisk = [];
      risks.forEach((risk) => {
        let types = this.availableRiskTypes;
        if (this.selectedRating) {
          types = [];
          types.push(this.selectedRating);
        }
        types.forEach((rt) => {
          if (
            risk.riskCategoryId === rt.id &&
            risk.rateValue >= Math.abs(this.riskScoreRange[1]) &&
            risk.rateValue <= Math.abs(this.riskScoreRange[0])
          ) {
            const level = lodashFind(RISK_LEVELS, ["value", risk.rateValue]);
            itemRisk.push({
              fontColor: level.fontColor,
              bgColor: level.bgColor,
              name: risk.riskCategoryNameDisplay,
              order: rt.order,
            });
          }
        });
      });

      if (itemRisk.length) {
        itemRisk = lodashOrderBy(itemRisk, ["order"]);
        itemRisk.forEach((cr) => {
          htmlRatings += `
            <div class="pull-left" style="height:18px;">
              <div class="pull-left mr-1 text-center" style="width:8px; height:8px; border-radius:4px; color:${cr.fontColor}; background-color:${cr.bgColor};"></div>
              <div class="pull-left mr-2" style="position:relative; top:-3px;">${cr.name}</div>
              <div class="clearfix"></div>
            </div>
          `;
        });
      }

      if (!htmlRatings) {
        html += `A zone or sub rating match selected filters`;
      } else {
        html += htmlRatings;
      }
      html += `<div class="clearfix"></div></div>`;
      return html;
    },
    loadItems: lodashDebounce(async function () {
      this.loading = true;
      let mapped = [];
      let itemList = [];
      if (
        this.countries &&
        (this.selectedGeographyType === GEOGRAPHY_NAMES.country ||
          this.selectedGeographyType === GEOGRAPHY_NAMES.all)
      ) {
        itemList = itemList.concat(this.countries);
      }
      if (
        !this.selectedRegion &&
        this.waters &&
        this.hasMaritimeAccess &&
        (this.selectedGeographyType === GEOGRAPHY_NAMES.waters ||
          this.selectedGeographyType === GEOGRAPHY_NAMES.all)
      ) {
        itemList = itemList.concat(this.waters);
      }
      if (
        !this.selectedRegion &&
        this.ports &&
        this.hasMaritimeAccess &&
        (this.selectedGeographyType === GEOGRAPHY_NAMES.ports ||
          this.selectedGeographyType === GEOGRAPHY_NAMES.all)
      ) {
        itemList = itemList.concat(this.ports);
      }
      if (
        this.cities &&
        this.hasCitySummaryAccess &&
        (this.selectedGeographyType === GEOGRAPHY_NAMES.cities ||
          this.selectedGeographyType === GEOGRAPHY_NAMES.all)
      ) {
        itemList = itemList.concat(this.cities);
      }
      if (itemList && itemList.length > 0) {
        itemList.forEach((item) => {
          const props = item && item.properties ? item.properties : null;
          if (
            (props && this.isInRiskScoreRange(props.ratings)) ||
            this.zoneInRiskScoreRange(item)
          ) {
            if (
              (!this.search || (this.search && this.isInSearch(props))) &&
              (!this.selectedRegion ||
                (this.selectedRegion &&
                  props.parent.code === this.selectedRegion.value))
            ) {
              this.$set(mapped, mapped.length, props);
            }
          }
        });
      }
      this.items = mapped ? lodashOrderBy(mapped, ["name"]) : null;
      this.loading = false;
      let riskTypeToMap = await getMapTypeBySelectedRatings(
        this.selectedRating
      );
      if (riskTypeToMap && this.enabled) {
        const riskScoreRange = [];
        riskScoreRange.push(Math.abs(this.riskScoreRange[1]));
        riskScoreRange.push(Math.abs(this.riskScoreRange[0]));
        const params = {
          map: this.map,
          mapClickEvent: this.mapClickEvent,
          riskScoresToDisplay: riskScoreRange,
          riskType: riskTypeToMap,
          countries: this.countries,
          zones: this.zones,
        };

        if (this.hasMaritimeAccess) {
          const riskRatingCategories = await getRiskRatingCategories();
          const riskRatingMaritime = [];
          riskRatingCategories.forEach((rr) => {
            if (
              rr.id === RISK_RATING_PRIMARIES.maritime ||
              rr.parentId === RISK_RATING_PRIMARIES.maritime
            ) {
              riskRatingMaritime.push(rr.id);
            }
          });
          if (riskRatingMaritime.includes(riskTypeToMap)) {
            params.waters = this.waters;
            params.zones = null;
          }
        }

        this.attemptAddToMap(params);
      } else {
        removeFromMap({ map: this.map });
      }
      // To select first country in the list by default
      // if (!this.selectedData && this.items.length > 0) {
      //   this.handleClickSelect(this.items[0]);
      // }
    }, 500),
    async attemptAddToMap(params, attemptCount = 0) {
      if (!this.map && attemptCount < 50) {
        setTimeout(() => {
          this.attemptAddToMap(params, attemptCount + 1);
        }, 500);
      } else {
        await waitForMapReady(this.map);
        params.map = this.map;
        addToMap(params);
      }
    },
    isInRiskScoreRange(ratings) {
      if (ratings && Array.isArray(ratings)) {
        for (let i = 0; i < ratings.length; i++) {
          const rating = ratings[i];
          if (this.selectedRating) {
            if (
              rating.riskCategoryId === this.selectedRating.id &&
              rating.rateValue >= Math.abs(this.riskScoreRange[1]) &&
              rating.rateValue <= Math.abs(this.riskScoreRange[0])
            ) {
              return true;
            }
          } else {
            const riskType = lodashFind(this.availableRiskTypes, [
              "id",
              rating.riskCategoryId,
            ]);
            const subRiskType = lodashFind(this.availableSubRiskTypes, [
              "id",
              rating.riskCategoryId,
            ]);
            if (
              (riskType || subRiskType) &&
              rating.rateValue >= Math.abs(this.riskScoreRange[1]) &&
              rating.rateValue <= Math.abs(this.riskScoreRange[0])
            ) {
              return true;
            }
          }
        }
      }
      return false;
    },
    zoneInRiskScoreRange(item) {
      if (item) {
        const zones = lodashFilter(this.zones, [
          "properties.parent.code",
          item.properties.code,
        ]);
        if (zones && Array.isArray(zones)) {
          for (let i = 0; i < zones.length; i++) {
            const zone = zones[i];
            if (
              zone?.properties?.ratings &&
              Array.isArray(zone.properties.ratings) &&
              this.isInRiskScoreRange(zone.properties.ratings)
            ) {
              return true;
            }
          }
        }
      }
      return false;
    },
    isInSearch(item) {
      let isIn = false;
      if (this.search && item) {
        isIn =
          item.name &&
          item.name.toLowerCase().includes(this.search.toLowerCase());
      }
      return isIn;
    },
    async mapClickEvent(e, features) {
      e.originalEvent.stopPropagation();
      if (features.length > 0) {
        const country = this.getRiskRatingsCountry(features[0]);
        if (lodashGet(country, "properties.iso2Code")) {
          features[0] = country;
          // grab the country feature from hyperion's country list
          const result = await getCountryByCode(country.properties.iso2Code);
          const feature = result ? JSON.parse(result) : null;
          if (feature && feature.data) {
            features[1] = feature.data;
          }
        }
        this.detailsSidebar.features = features;
        this.detailsSidebar.visible = true;
        this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.monitor;
        this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.monitor;
        this.detailsSidebar.monitorType =
          DETAIL_SIDEBAR_TYPES.controlRisksRatings;
        this.$store.dispatch(
          MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
          this.detailsSidebar
        );
      }
    },
    getRiskRatingsCountry(feature) {
      let rrCountry;
      if (feature?.properties) {
        let countryISO2;
        if (feature.properties.CNTRY_ISO2) {
          countryISO2 = feature.properties.CNTRY_ISO2;
        } else if (feature.properties.CR_ID) {
          countryISO2 = feature.properties.CR_ID;
        }
        const zoneID = lodashGet(feature, "properties.Zone_ID");
        if (this.countries && Array.isArray(this.countries)) {
          if (countryISO2) {
            const country = lodashFind(this.countries, [
              "properties.iso2Code",
              countryISO2.toUpperCase(),
            ]);
            if (country) {
              rrCountry = country;
            }
          }
          if (zoneID) {
            // if user clicks on a country's zone then get the zone_id and find the country
            const zone = lodashFind(this.zones, [
              "properties.code",
              zoneID.toUpperCase(),
            ]);
            if (lodashGet(zone, "properties.parent.code")) {
              rrCountry = lodashFind(this.countries, [
                "properties.code",
                zone.properties.parent.code,
              ]);
            }
          }
        }
      }
      if (rrCountry) {
        return rrCountry;
      }
      return null;
    },
    handleClickSelect(item) {
      this.selectedItem = item;
      this.selectedItem.selectedPort = null;
      this.selectedItem.selectedCity = null;
      if (this.selectedItem?.code) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.control_risk_risk_ratings,
          EVENT_ACTIONS.clicked,
          "Geography - " +
            this.selectedItem.type +
            ": " +
            this.selectedItem.code
        );
      }
      if (
        this.selectedItem.type === GRIDS_STABILITY_TYPES.country &&
        this.selectedItem?.iso2Code
      ) {
        this.geographyFilters = this.selectedItem?.iso2Code;
        this.$emit(EMITTED_ACTIONS.SELECTED, this.selectedItem);
      }
      if (
        this.hasMaritimeAccess &&
        this.selectedItem.type === GRIDS_STABILITY_TYPES.water &&
        this.selectedItem?.code
      ) {
        this.geographyFilters = this.selectedItem?.code;
        this.$emit(EMITTED_ACTIONS.SELECTED, this.selectedItem);
      }
      if (
        this.hasMaritimeAccess &&
        this.selectedItem.type === GRIDS_STABILITY_TYPES.port &&
        this.selectedItem?.parent?.code
      ) {
        const rrCountry = lodashFind(this.countries, [
          "properties.code",
          this.selectedItem.parent.code,
        ]);
        if (rrCountry?.properties?.iso2Code) {
          this.geographyFilters = this.selectedItem?.code;
          const portInfo = lodashCloneDeep(this.selectedItem);
          this.selectedItem = lodashCloneDeep(rrCountry.properties);
          this.selectedItem.selectedPort = portInfo;
          this.selectedItem.selectedCity = null;
          this.$emit(EMITTED_ACTIONS.SELECTED, this.selectedItem);
        }
      }
      if (
        this.hasCitySummaryAccess &&
        this.selectedItem.type === GRIDS_STABILITY_TYPES.city &&
        this.selectedItem?.parent?.code
      ) {
        const rrCountry = lodashFind(this.countries, [
          "properties.code",
          this.selectedItem.parent.code,
        ]);
        if (rrCountry?.properties?.iso2Code) {
          this.geographyFilters = this.selectedItem?.code;
          const cityInfo = lodashCloneDeep(this.selectedItem);
          this.selectedItem = lodashCloneDeep(rrCountry.properties);
          this.selectedItem.selectedCity = cityInfo;
          this.selectedItem.selectedPort = null;
          this.$emit(EMITTED_ACTIONS.SELECTED, this.selectedItem);
        }
      }
    },
    handleChangeRating(rating) {
      if (rating) {
        this.selectedRating = rating;
        this.updateRatingSelectionInPreferences();
        this.loadItems();
        this.handleSuggestedGeography();

        const selectorPanel = this.selectorPanel;
        selectorPanel.powerOutage = false;
        this.$store.dispatch(
          MODULE_PREFIX_HOME + GET_SELECTOR_PANEL,
          selectorPanel
        );
        this.$store.dispatch(
          MODULE_PREFIX_POWER_OUTAGE + GET_POWER_OUTAGE_REFRESH_MAP,
          true
        );
      }
    },
    handleClickClearRating() {
      this.selectedRating = null;
      this.updateRatingSelectionInPreferences();
      this.loadItems();
      this.handleSuggestedGeography();
    },
    updateRatingSelectionInPreferences() {
      let preferences = populateDefaultPreferences(this.preferences);
      preferences.controlRisks.riskRatings.ratings = this.selectedRating;
      preferences.powerOutage = preferences.powerOutage || {};
      preferences.powerOutage.enabled = false;
      this.$store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, preferences);
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.control_risk_risk_ratings,
        EVENT_ACTIONS.clicked,
        `Type - ${this.selectedRating ? this.selectedRating.name : "None"}`
      );
    },
    handleChangeRiskScoreRange() {
      this.updateRiskScoreRangeInPreferences();
      this.loadItems();
      this.handleSuggestedGeography();
      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.control_risk_risk_ratings,
        EVENT_ACTIONS.clicked,
        `Levels - [${this.riskScoreRange}]`
      );
    },
    updateRiskScoreRangeInPreferences() {
      let preferences = populateDefaultPreferences(this.preferences);
      preferences.controlRisks.riskRatings.riskScoreRange = this.riskScoreRange;
      this.$store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, preferences);
    },
    handleChangeRegion() {
      this.loadItems();
      this.handleSuggestedGeography();
      if (lodashGet(this.selectedRegion, "text")) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.control_risk_risk_ratings,
          EVENT_ACTIONS.clicked,
          "Region - " + this.selectedRegion.text
        );
      }
    },
    handleClickClearRegion() {
      this.selectedRegion = null;
      this.handleChangeRegion();
    },
    handleKeyUpSearch(event) {
      // validate if the user has pressed enter and geographyFilters have value
      // then validate if there is one item in geographyFilters and one item in the items list
      // call handleGeographyData() to get the geography, handleClickSelect(geography) to get the geography details and clean the search.
      if (event.keyCode === 13 && this.geographyFilters) {
        if (this.geographyFilters.length === 2 && this.items.length === 1) {
          let geography = this.handleGeographyData();
          this.handleClickSelect(geography);
          this.search = "";
        }
      } else {
        this.loadItems();
      }
    },
    async handleClickClearSearch() {
      this.search = "";
      await this.loadItems();
      this.handleSuggestedGeography();
      this.selectedItem = null;
    },
    async handleEnabledChange() {
      let preferences = populateDefaultPreferences(this.preferences);
      preferences.controlRisks.riskRatings.mapEnabled = this.enabled;
      this.$store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, preferences);
      if (!this.enabled) {
        removeFromMap({ map: this.map });
      } else {
        await this.loadItems();
      }
    },
    readPreferences() {
      let prefs = populateDefaultPreferences(this.preferences);
      if (
        lodashGet(prefs, "controlRisks.riskRatings.ratings") &&
        typeof prefs.controlRisks.riskRatings.ratings === "object"
      ) {
        this.selectedRating = prefs.controlRisks.riskRatings.ratings;
      }
      if (lodashGet(prefs, "controlRisks.riskRatings.riskScoreRange")) {
        this.riskScoreRange = prefs.controlRisks.riskRatings.riskScoreRange;
      }
    },
    async initialize() {
      this.loading = true;
      this.readPreferences();
      const {
        countries,
        zones,
        waters,
        ports,
        cities,
      } = await getRiskRatings();
      this.countries = countries;

      this.zones = zones;
      this.regions =
        (await getAllRegions())?.map((r) => ({
          value: r.properties.code,
          text: r.properties.name,
          countries: r.properties.countries.map((c) => ({
            value: c.isoAlpha2Code,
            text: c.name,
          })),
        })) || [];

      if (this.hasMaritimeAccess) {
        this.waters = waters;
        this.ports = ports;
      }

      if (this.hasCitySummaryAccess) {
        this.cities = cities;
      }

      await this.getFilteredRiskTypes();
      await this.loadItems();
      this.handleSuggestedGeography();
      this.handleSelectedData();
      this.loading = false;
    },
    handleSelectedData() {
      if (this.selectedData?.code) {
        let item;
        if (
          this.selectedData.code.length === 3 ||
          this.selectedData.code.length === 2
        ) {
          this.selectedGeographyType = GEOGRAPHY_NAMES.country;

          item = lodashFind(this.countries, [
            "properties.code",
            this.selectedData.code.toUpperCase(),
          ]);
          if (!item) {
            item = lodashFind(this.countries, [
              "properties.iso2Code",
              this.selectedData.code.toUpperCase(),
            ]);
          }
        } else if (this.selectedData.code.length === 4) {
          this.selectedGeographyType = GEOGRAPHY_NAMES.waters;
          item = this.waters.find(
            (w) =>
              w.properties.code.toUpperCase() ===
              this.selectedData.code.toUpperCase()
          );
        }

        if (item) {
          this.handleClickSelect(item.properties);
        } else {
          this.handleClickSelect();
        }
      }
    },
    handleGeographyTypeChange() {
      this.geographyFilters = [];
    },
    handleGeographyChange() {
      if (this.geographyFilters) {
        let geography = this.handleGeographyData();
        this.handleClickSelect(geography);
        this.items = [geography];
      } else {
        this.handleClickClearSearch();
      }
    },
    handleGeographyData() {
      const country = lodashFind(this.countries, [
        "properties.iso2Code",
        this.geographyFilters.toUpperCase(),
      ]);
      if (country) {
        return country?.properties;
      }
      if (this.hasMaritimeAccess) {
        const water = lodashFind(this.waters, [
          "properties.code",
          this.geographyFilters.toUpperCase(),
        ]);
        if (water) {
          return water?.properties;
        }
        const port = lodashFind(this.ports, [
          "properties.code",
          this.geographyFilters,
        ]);
        if (port) {
          return port?.properties;
        }
      }
      if (this.hasCitySummaryAccess) {
        const city = lodashFind(this.cities, [
          "properties.code",
          this.geographyFilters,
        ]);
        if (city) {
          return city?.properties;
        }
      }

      return null;
    },
    handleSuggestedGeography() {
      // load the suggestedItems list based on risk, levels and regions.
      let mapped = [];
      let itemList = [];
      if (
        this.countries &&
        (this.selectedGeographyType === GEOGRAPHY_NAMES.country ||
          this.selectedGeographyType === GEOGRAPHY_NAMES.all)
      ) {
        itemList = itemList.concat(this.countries);
      }
      if (
        !this.selectedRegion &&
        this.waters &&
        this.hasMaritimeAccess &&
        (this.selectedGeographyType === GEOGRAPHY_NAMES.waters ||
          this.selectedGeographyType === GEOGRAPHY_NAMES.all)
      ) {
        itemList = itemList.concat(this.waters);
      }
      if (
        !this.selectedRegion &&
        this.ports &&
        this.hasMaritimeAccess &&
        (this.selectedGeographyType === GEOGRAPHY_NAMES.ports ||
          this.selectedGeographyType === GEOGRAPHY_NAMES.all)
      ) {
        itemList = itemList.concat(this.ports);
      }
      if (
        this.cities &&
        this.hasCitySummaryAccess &&
        (this.selectedGeographyType === GEOGRAPHY_NAMES.cities ||
          this.selectedGeographyType === GEOGRAPHY_NAMES.all)
      ) {
        itemList = itemList.concat(this.cities);
      }
      if (itemList && itemList.length > 0) {
        itemList.forEach((item) => {
          const props = item && item.properties ? item.properties : null;
          if (
            (props && this.isInRiskScoreRange(props.ratings)) ||
            this.zoneInRiskScoreRange(item)
          ) {
            if (
              !this.selectedRegion ||
              (this.selectedRegion &&
                props.parent.code === this.selectedRegion.value)
            ) {
              this.$set(mapped, mapped.length, props);
            }
          }
        });
      }

      //mapped the suggestedItems list to its structure
      this.suggestedItems = mapped?.map((i) => ({
        text: i.name,
        value: i.iso2Code || i.code,
      }));

      // validate that is not null, if not then sort the list by name ( text )
      if (this.suggestedItems) {
        this.suggestedItems.sort((a, b) => a.text.localeCompare(b.text));
      }
    },
  },
  watch: {
    async enabled() {
      this.handleEnabledChange();
    },
    async selectedData() {
      await this.handleClickClearSearch();
      this.handleSelectedData();
    },
    selectedGeographyType: {
      async handler() {
        this.search = "";
        this.selectedItem = null;
        this.selectedGeographyLabel = "Search " + this.selectedGeographyType;
        this.loadItems();
        this.handleSuggestedGeography();
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.control_risk_risk_ratings,
          EVENT_ACTIONS.clicked,
          "Geography - " + this.selectedGeographyType
        );
      },
    },
    refreshDataMap: {
      async handler() {
        if (lodashGet(this.preferences, "controlRisks.riskRatings.ratings")) {
          await this.loadItems();
        } else {
          this.selectedRating = null;
          this.handleEnabledChange();
        }
      },
      deep: true,
    },
    "selectorPanel.selected": {
      handler() {
        if (!this.selectorPanel.selected) {
          this.selectedItem = null;
          this.$emit(EMITTED_ACTIONS.SELECTED, this.selectedItem);
        }
      },
    },
    resetMapIcons: {
      handler(newValue, oldValue) {
        if (oldValue !== newValue) {
          const mapLayerId = "layer-rs";
          removeMapData(this.map, `${mapLayerId}-hotspots`);
          const eventLayers = getVerifiedEventTopicLayers();
          lodashForOwn(eventLayers, (value, key) => {
            removeMapData(this.map, `${mapLayerId}-topic-${key}`);
          });
        }
      },
    },
  },
};
</script>

<style>
.hyp-risks-selector-container {
  width: 275px !important;
}
.hyp-pulse-risk-container-grd .hyp-risks-selector-container {
  width: 252px !important;
}
.risk-score-range {
  width: 250px !important;
  margin-left: 10px !important;
}
.risk-score-range .v-slider__thumb {
  width: 16px !important;
  height: 16px !important;
  left: -8px !important;
  border: 1px solid #808080 !important;
  background-color: transparent !important;
}
.risk-score-range .v-slider__track-fill {
  background-color: #808080 !important;
}
.risk-score-range .v-slider__track-background {
  background-color: rgb(149 149 149 / 26%) !important;
}
.risk-score-range .v-slider__tick-label {
  font-size: 10px !important;
  color: rgba(0, 0, 0, 0.6) !important;
}
.risk-score-range .v-slider__ticks-container > span:nth-child(5) {
  background-color: #4dc400 !important;
}
.risk-score-range .v-slider__ticks-container > span:nth-child(4) {
  background-color: #add600 !important;
}
.risk-score-range .v-slider__ticks-container > span:nth-child(3) {
  background-color: #ffc600 !important;
}
.risk-score-range .v-slider__ticks-container > span:nth-child(2) {
  background-color: #ff6700 !important;
}
.risk-score-range .v-slider__ticks-container > span:nth-child(1) {
  background-color: #c10000 !important;
}
.risk-score-range
  .v-slider__ticks-container
  > span:first-child
  .v-slider__tick-label {
  margin-left: -20px !important;
}
.risk-score-range
  .v-slider__ticks-container
  > span:last-child
  .v-slider__tick-label {
  margin-left: 20px !important;
}
.risk-score-range .v-slider__tick {
  border-radius: 4px !important;
}
.rs-group-container .v-list-item .v-list-item__subtitle {
  line-height: 1.3;
}
</style>

<style scoped>
.card-title {
  font-size: 16px !important;
}
.card-subtitle {
  font-size: 12px !important;
}
.list-container {
  height: 40vh;
  overflow-y: auto;
}
.risk-cat-name {
  font-size: 11px;
}
.risk-cat-bar {
  width: 40px;
  height: 24px;
}
.levels-label {
  color: rgba(0, 0, 0, 0.6) !important;
}
</style>
